import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AppState, AppSettings, Position, MapMode, POICandidate } from '../types'
import type { SupportedLanguage } from '../utils/constants'

export interface POIHistoryEntry extends POICandidate {
  timestamp: number
  spoken: string
}

interface AppStore extends AppState {
  // Actions
  setStatus: (status: string) => void
  setLastLocation: (location: string) => void
  setHeading: (heading: number | null) => void
  setLastPOI: (poi: string) => void
  setLastSpoken: (spoken: string) => void
  setIsTracking: (isTracking: boolean) => void
  updateSettings: (settings: Partial<AppSettings>) => void
  setPosition: (position: Position | null) => void
  getPosition: () => Position | null
  setMapMode: (mode: MapMode) => void
  getMapMode: () => MapMode
  poiPosition: { lat: number; lon: number; title: string } | null
  setPoiPosition: (poi: { lat: number; lon: number; title: string } | null) => void
  // New features
  language: SupportedLanguage
  setLanguage: (lang: SupportedLanguage) => void
  poiHistory: POIHistoryEntry[]
  addPOIHistory: (poi: POICandidate, spoken: string) => void
  clearPOIHistory: () => void
  routePositions: Array<Position & { accuracy?: number }>
  addRoutePosition: (position: Position, accuracy?: number) => void
  clearRoute: () => void
  routeStartTime: number | null
  setRouteStartTime: (time: number | null) => void
  selectedPOI: POICandidate | null
  setSelectedPOI: (poi: POICandidate | null) => void
  nearbyPOIs: POICandidate[]
  setNearbyPOIs: (pois: POICandidate[]) => void
  pinnedLocations: Array<{ id: string; lat: number; lon: number; timestamp: number }>
  addPinnedLocation: (lat: number, lon: number) => void
  removePinnedLocation: (id: string) => void
  clearPinnedLocations: () => void
  mapType: 'osm' | 'satellite' | 'terrain' | 'dark'
  setMapType: (type: 'osm' | 'satellite' | 'terrain' | 'dark') => void
  pinMode: boolean
  setPinMode: (enabled: boolean) => void
  compassLocked: boolean
  setCompassLocked: (locked: boolean) => void
  manualRotation: number | null
  setManualRotation: (angle: number | null) => void
  narratingPOI: string | null
  setNarratingPOI: (key: string | null) => void
  narrationError: string | null
  setNarrationError: (error: string | null) => void
  savedMapView: { center: [number, number]; zoom: number } | null
  setSavedMapView: (view: { center: [number, number]; zoom: number } | null) => void
  isFetchingPOIs: boolean
  setIsFetchingPOIs: (isFetching: boolean) => void
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      status: 'Idle',
      lastLocation: '-',
      heading: null,
      lastPOI: '-',
      lastSpoken: '-',
      isTracking: false,
      settings: {
        radio: true, // On by default
        useGoogle: false,
        radius: 500, // Default radius: 500m
        autoUpdate: true, // Auto-update every 2 minutes by default (replaces follow)
        showPOIs: true, // Show POIs on map by default
      },
      position: null,
      mapMode: 'north',
      poiPosition: null,
      language: 'nl',
      poiHistory: [],
      routePositions: [],
      routeStartTime: null,
      selectedPOI: null,
      nearbyPOIs: [],
      pinnedLocations: [],
      mapType: 'osm',
      pinMode: false,
      compassLocked: false,
      manualRotation: null,
      narratingPOI: null,
      narrationError: null,
      savedMapView: null,
      isFetchingPOIs: false,

      // Actions
      setStatus: (status) => set({ status }),
      setLastLocation: (location) => set({ lastLocation: location }),
      setHeading: (heading) => set({ heading }),
      setLastPOI: (poi) => set({ lastPOI: poi }),
      setLastSpoken: (spoken) => set({ lastSpoken: spoken }),
      setIsTracking: (isTracking) => set({ isTracking }),
      updateSettings: (settings) =>
        set((state) => ({
          settings: { ...state.settings, ...settings },
        })),
      setPosition: (position) => set({ position }),
      getPosition: () => get().position,
      setMapMode: (mode) => set({ mapMode: mode }),
      getMapMode: () => get().mapMode,
      setPoiPosition: (poi) => set({ poiPosition: poi }),
      setLanguage: (lang) => set({ language: lang }),
      addPOIHistory: (poi, spoken) =>
        set((state) => ({
          poiHistory: [
            { ...poi, timestamp: Date.now(), spoken },
            ...state.poiHistory.slice(0, 49), // Keep last 50
          ],
        })),
      clearPOIHistory: () => set({ poiHistory: [] }),
      addRoutePosition: (position, accuracy) =>
        set((state) => ({
          routePositions: [
            ...state.routePositions,
            { ...position, accuracy },
          ].slice(-1000), // Keep last 1000 positions
        })),
      clearRoute: () => set({ routePositions: [], routeStartTime: null }),
      setRouteStartTime: (time) => set({ routeStartTime: time }),
      setSelectedPOI: (poi) => set({ selectedPOI: poi }),
      setNearbyPOIs: (pois) => set({ nearbyPOIs: pois }),
      addPinnedLocation: (lat, lon) =>
        set((state) => ({
          pinnedLocations: [
            ...state.pinnedLocations,
            { id: `pin-${Date.now()}`, lat, lon, timestamp: Date.now() },
          ],
        })),
      removePinnedLocation: (id) =>
        set((state) => ({
          pinnedLocations: state.pinnedLocations.filter((p) => p.id !== id),
        })),
      clearPinnedLocations: () => set({ pinnedLocations: [] }),
      setMapType: (type) => set({ mapType: type }),
      setPinMode: (enabled) => set({ pinMode: enabled }),
      setCompassLocked: (locked) => set({ compassLocked: locked }),
      setManualRotation: (angle) => set({ manualRotation: angle }),
      setNarratingPOI: (key) => set({ narratingPOI: key }),
      setNarrationError: (error) => set({ narrationError: error }),
      setSavedMapView: (view) => set({ savedMapView: view }),
      setIsFetchingPOIs: (isFetching) => set({ isFetchingPOIs: isFetching }),
    }),
    {
      name: 'periscope-storage',
      partialize: (state) => ({
        settings: state.settings,
        language: state.language,
        poiHistory: state.poiHistory.slice(0, 20), // Only persist last 20
        pinnedLocations: state.pinnedLocations, // Persist all pinned locations
        savedMapView: state.savedMapView, // Persist last map position
      }),
    }
  )
)
